import type { FunctionComponent } from 'react';
import type { Control } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconPlus } from '~/ui/core/icons';

import { CompetencySearchContent } from './skills/CompetenciesSuggestions';
import { SkillSearchContent } from './skills/SkillSearch';
import type { ProfileForm } from './types';

type EditSkillsAndCompetenciesOptionsProps = {
    readonly architectureId?: string;
    readonly control: Control<ProfileForm>;
    readonly currentSkillCount: number;
    readonly hiddenSkillIds?: ReadonlySet<string>;
    readonly isSkillLimitReached: boolean;
    readonly itemType: 'job-level' | 'job-role';
    readonly maximumSkillLimit: number | undefined;
    readonly onCompetencyChange: () => void;
    readonly onSkillChange: () => void;
};

export const EditSkillsAndCompetenciesOptions: FunctionComponent<EditSkillsAndCompetenciesOptionsProps> = ({
    architectureId,
    control,
    currentSkillCount,
    hiddenSkillIds,
    isSkillLimitReached,
    itemType,
    maximumSkillLimit,
    onCompetencyChange,
    onSkillChange,
}) => {
    const translate = useTranslate();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button iconPosition="leading" size="sm" variant="secondary">
                    <IconPlus />
                    {translate('common.edit')}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={5}>
                {isSkillLimitReached ? (
                    <DropdownMenuItem
                        disabled={true}
                        tooltipContent={translate(
                            itemType === 'job-role'
                                ? 'pages.job-architecture-v2.skill-limit-reached-role'
                                : 'pages.job-architecture-v2.skill-limit-reached-level',
                            {
                                current: currentSkillCount,
                                maximum: maximumSkillLimit,
                            },
                        )}
                    >
                        {translate('pages.job-architecture-v2.job-profile-skill-suggestions-add-label')}
                    </DropdownMenuItem>
                ) : (
                    <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                            {translate('pages.job-architecture-v2.job-profile-skill-suggestions-add-label')}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                            <SkillSearchContent
                                architectureId={architectureId}
                                control={control}
                                hiddenSkillIds={hiddenSkillIds}
                                onSkillChange={onSkillChange}
                            />
                        </DropdownMenuSubContent>
                    </DropdownMenuSub>
                )}
                <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                        {translate('pages.job-architecture-v2.add-competency-button')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                        <CompetencySearchContent
                            architectureId={architectureId}
                            control={control}
                            onCompetencyChange={onCompetencyChange}
                        />
                    </DropdownMenuSubContent>
                </DropdownMenuSub>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
